<template>
  <panel-layout
    id="campaign-panel"
    :loading="loading"
    :shouldDismiss="() => !unsavedChanges"
    :didAttemptToDismiss="cancel"
  >
    <template #heading>
      <h2 class="text-lg md:text-xl tracking-tight font-bold leading-none">
        {{ $t('pages.marketing.campaigns.edit.title') }}
      </h2>
    </template>

    <template #actions-right>
      <div v-if="campaign && hasCampaignType && !isNewRecord" class="flex items-center space-x-1">
        <UTooltip
          v-if="$grants.all({ marketing: ['send'] })"
          :text="$t('general.sendPreview')"
        >
          <UButton
            size="xs"
            color="neutral"
            variant="ghost"
            icon="lucide:eye"
            @click="handlePreview"
          />
        </UTooltip>

        <UTooltip
          v-if="$grants.all({ marketing: ['send'] })"
          :text="isCampaignTypeEmail ? $t('general.sendEmail') : $t('general.sendSMS')"
        >
          <UButton
            size="xs"
            color="neutral"
            variant="ghost"
            icon="lucide:send"
            :disabled="!campaignHasCustomers(campaign)"
            @click="handleSend(false)"
          />
        </UTooltip>

        <UTooltip
          v-if="$grants.all({ marketing: ['edit'] })"
          :text="$t('pages.marketing.campaigns.edit.saveDraft')"
        >
          <UButton
            size="xs"
            color="neutral"
            variant="ghost"
            icon="lucide:save"
            @click="handleSave(false)"
          />
        </UTooltip>
      </div>
    </template>

    <div v-if="campaign && hasCampaignType && ready" class="h-full">
      <o-scroll class="bg-gray-50 dark:bg-gray-900">
        <div class="h-full flex flex-col">
          <div class="flex flex-col divide-y border-b border-gray-200 dark:border-gray-800">
            <o-form ref="form">
              <o-alert
                v-if="!respect"
                show
                :title="$t('pages.marketing.campaigns.edit.transactional')"
                variant="warning"
                class="text-sm mx-6 my-2"
              >
                <p v-html="$t('pages.marketing.campaigns.edit.transactionalInfo')" />
              </o-alert>

              <div class="flex items-center py-2 px-6">
                <p class="opacity-60 text-sm font-medium w-32 flex-shrink-0">
                  {{ $t('general.customers') }}
                </p>
                <div class="flex-grow">
                  <customers v-model:campaign="campaign" />
                </div>
              </div>
              <div
                v-if="isCampaignTypeEmail"
                class="flex items-center py-2 px-6"
              >
                <p class="opacity-60 text-sm font-medium w-32 flex-shrink-0">
                  {{ $t('general.subject') }}
                </p>
                <div class="flex-grow">
                  <o-field-group v-slot="{ status }" rules="required">
                    <input
                      v-model="campaign.subject"
                      type="text"
                      name="mailSubject"
                      :placeholder="$t('general.subject')"
                      class="w-full text-sm opacity-80 bg-transparent -mx-2 p-2 rounded-md focus:ring-2 focus:bg-white dark:focus:bg-gray-800"
                      :class="{
                        'placeholder:text-red-500': status === 'error'
                      }"
                    >
                  </o-field-group>
                </div>
              </div>

              <div class="flex items-start py-2 px-6">
                <p class="opacity-60 text-sm font-medium w-32 flex-shrink-0">
                  {{ $t('general.type') }}
                </p>
                <div class="flex-grow flex items-center hover">
                  <div class="text-sm flex-grow">
                    <template v-if="campaign.isContentTypeMarketing">
                      <p>{{ $t('general.marketing') }}</p>
                      <p class="text-gray-500 dark:text-gray-400">
                        {{ $t('marketing.notToUnsubscribed') }}
                      </p>
                    </template>

                    <template v-if="campaign.isContentTypeAdministrative">
                      <p>{{ $t('marketing.administrative') }}</p>
                      <p class="text-gray-500 dark:text-gray-400">
                        {{ $t('marketing.sendToAll') }}
                      </p>
                    </template>
                  </div>

                  <UDropdownMenu :items="campaignTypeItems">
                    <UButton
                      icon="lucide:pencil"
                      size="sm"
                      color="neutral"
                      variant="ghost"
                      class="flex-shrink-0"
                    >
                      {{ $t('pages.marketing.campaigns.edit.changeType') }}
                    </UButton>
                  </UDropdownMenu>
                </div>
              </div>

              <div v-if="isCampaignTypeEmail" class="flex items-start px-6 py-3">
                <p class="w-32 flex-shrink-0 pt-2 text-sm font-medium opacity-60">
                  {{ $t('pages.marketing.campaigns.edit.attachments') }}
                  <span v-if="campaign.attachments?.length">({{ campaign.attachments.length }})</span>
                </p>

                <div class="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                  <div
                    v-for="attachment in campaign.attachments"
                    :key="attachment.guid"
                    class="flex min-w-0 max-w-full items-center gap-2 rounded-lg bg-neutral-100 p-1  dark:border-gray-700 dark:bg-gray-800"
                  >
                    <div class="flex h-6 w-6 shrink-0 items-center justify-center overflow-hidden rounded-md bg-gray-100 dark:bg-gray-900">
                      <img
                        v-if="isImageAttachment(attachment)"
                        :src="attachment.url"
                        :alt="attachment.name"
                        class="h-full w-full object-cover"
                      >
                      <UIcon v-else name="lucide:files" class="size-4 text-gray-500 dark:text-gray-400" />
                    </div>

                    <p class="max-w-44 truncate text-sm text-gray-800 dark:text-gray-100">
                      {{ attachment.name }}
                    </p>

                    <div class="flex shrink-0 items-center border-l border-gray-200 pl-1 dark:border-gray-700 gap-1">
                      <UTooltip
                        v-if="isImageAttachment(attachment)"
                        :text="$t('pages.marketing.campaigns.edit.insertAttachment')"
                      >
                        <UButton
                          size="xs"
                          variant="ghost"
                          icon="lucide:plus"
                          :ui="{
                            leadingIcon: 'size-3'
                          }"
                          @click="handleInsertAttachment(attachment)"
                        />
                      </UTooltip>

                      <UTooltip :text="$t('pages.marketing.campaigns.edit.removeAttachment')">
                        <UButton
                          size="xs"
                          variant="ghost"
                          icon="lucide:trash"
                          :ui="{
                            leadingIcon: 'size-3'
                          }"
                          @click="handleRemoveAttachment(attachment)"
                        />
                      </UTooltip>
                    </div>
                  </div>

                  <UButton
                    icon="lucide:paperclip"
                    size="sm"
                    variant="soft"
                    :ui="{
                      leadingIcon: 'size-3'
                    }"
                    @click="handleAddAttachment"
                  >
                    {{ $t('pages.marketing.campaigns.edit.addAttachment') }}
                  </UButton>
                </div>
              </div>
            </o-form>
          </div>
          <div v-if="isCampaignTypeEmail" class="flex-grow flex-shrink h-10">
            <client-only>
              <o-email
                ref="article"
                v-model="campaign.body"
                :variables="campaignPlaceholders"
                :links="campaignPlaceholderLinks"
                :image="{
                  width: true,
                  multiple: false,
                  upload: handleUpload,
                  select: assets
                }"
                :placeholder="$t('pages.marketing.campaigns.edit.emailBody')"
                class="h-full"
              />
            </client-only>
          </div>
          <div v-if="isCampaignTypeSMS" class="flex-grow flex-shrink h-10">
            <client-only>
              <o-sms
                v-model="campaign.body"
                :variables="campaignPlaceholders"
                :placeholder="$t('pages.marketing.campaigns.edit.smsBody')"
                class="h-full"
              />
            </client-only>
          </div>
        </div>
      </o-scroll>
    </div>

    <div
      v-if="campaign && !hasCampaignType"
      class="h-1/2 flex flex-col space-y-2 items-center justify-center w-full max-w-xs mx-auto"
    >
      <UButton
        color="primary"
        variant="solid"
        icon="lucide:smartphone"
        class="w-full justify-center"
        @click="handleCreate('sms')"
      >
        {{ $t('pages.marketing.campaigns.edit.createSMSCampaign') }}
      </UButton>
      <UButton
        color="primary"
        variant="solid"
        icon="lucide:mail"
        class="w-full justify-center"
        @click="handleCreate('email')"
      >
        {{ $t('pages.marketing.campaigns.edit.createEmailCampaign') }}
      </UButton>
    </div>

    <template v-if="campaign">
      <send-dialog ref="sendDialog" @success="cancel(true)" />

      <add-attachment-dialog
        ref="addAttachmentDialog"
        v-model:attachments="campaign.attachments"
      />
    </template>

    <template #footer>
      <div v-if="campaign">
        <div class="flex justify-between align-items">
          <div class="flex items-center space-x-1">
            <UButton color="neutral" variant="ghost" class="mr-2" @click="cancel()">
              {{ $t('general.cancel') }}
            </UButton>
            <UTooltip
              v-if="hasCampaignType && $grants.all({ marketing: ['send'] })"
              :text="$t('general.sendPreview')"
            >
              <UButton
                color="primary"
                variant="outline"
                icon="lucide:eye"
                @click="handlePreview"
              >
                {{ $t('general.sendPreview') }}
              </UButton>
            </UTooltip>
          </div>
          <div v-if="hasCampaignType" class="flex items-center space-x-1">
            <UTooltip
              v-if="$grants.all({ marketing: ['send'] })"
              :text="isCampaignTypeEmail ? $t('pages.marketing.campaigns.edit.scheduleEmail') : $t('pages.marketing.campaigns.edit.scheduleSMS')"
            >
              <UButton
                color="primary"
                variant="soft"
                icon="lucide:calendar-clock"
                :disabled="!campaignHasCustomers(campaign)"
                @click="handleSend(true)"
              >
                {{ $t('pages.marketing.campaigns.edit.scheduleForLater') }}
              </UButton>
            </UTooltip>

            <UTooltip
              v-if="$grants.all({ marketing: ['send'] })"
              :text="isCampaignTypeEmail ? $t('general.sendEmail') : $t('general.sendSMS')"
            >
              <UButton
                color="primary"
                variant="solid"
                icon="lucide:send"
                :disabled="!campaignHasCustomers(campaign)"
                @click="handleSend(false)"
              >
                {{ $t('pages.marketing.campaigns.edit.sendNow') }}
              </UButton>
            </UTooltip>
          </div>
        </div>
      </div>
    </template>

    <o-dialog
      ref="confirmDialog"
      :title="$t('general.unsavedChanges')"
      :info="$t('general.unsavedChangesInfo')"
      :options="[
        { text: $t('general.back'), type: 'cancel' },
        {
          text: $t('general.closeWithoutSaving'),
          type: 'confirm',
          variant: undefined,
          flat: true,
          outline: true,
          callback: () => cancel(true)
        },
        {
          text: $t('general.saveAndClose'),
          type: 'confirm',
          response: 'save',
          callback: () => handleSave(true)
        }
      ]"
      :portal="panel.portal"
    />
  </panel-layout>
</template>
<script setup lang="ts">
// @ts-ignore
import SendDialog from '@/components/campaign/actions/Send.vue'
// @ts-ignore
import AddAttachmentDialog from '@/components/campaign/actions/AddAttachment.vue'

// @ts-ignore
import PanelLayout from '@/components/panel/PanelLayout.vue'
// @ts-ignore
import Customers from '@/components/campaign/elements/Customer.vue'

import { CampaignContentType, CampaignType } from '~/_api/models/campaign'

import { usePanel } from '~/composables/usePanel'

const serviceStore = useServiceStore()
const employeeStore = useEmployeeStore()

const { $api, $auth, $notification, $i18n, $bus, $grants } = useNuxtApp()
const router = useRouter()

const panel = usePanel()

const props = defineProps<{
  id?: string
  customers?: any[] | null
}>()

const campaign = ref<any>(null)
const respect = ref(true)
const service = ref<any[]>([])
const employee = ref<any[]>([])
const assets = ref<any[]>([])
const ready = ref(false)
const loading = ref(true)

const form = ref<any>()
const sendDialog = ref<any>()
const addAttachmentDialog = ref<any>()
const article = ref<any>()
const confirmDialog = ref<any>()

const services = computed(() => serviceStore.all)
const employees = computed(() => employeeStore.employeesTypeEmployee)

const { unsavedChanges, snapshot } = useUnsavedChanges(campaign)

const isNewRecord = computed(() => {
  return campaign.value.isNewRecord
})

const isCampaignTypeEmail = computed(() => {
  return campaign.value.isCampaignTypeEmail
})

const isCampaignTypeSMS = computed(() => {
  return campaign.value.isCampaignTypeSMS
})

const hasCampaignType = computed(() => {
  return isCampaignTypeEmail.value || isCampaignTypeSMS.value
})

const campaignOptions = computed(() => {
  return campaign.value?.options ?? {}
})

const campaignPlaceholders = computed(() => {
  return campaignOptions.value.placeholders ?? []
})

const campaignPlaceholderLinks = computed(() => {
  return campaignOptions.value.placeholderLinks ?? []
})

const campaignCustomerCount = (model: any) => {
  if (Array.isArray(model?.customerIDs)) {
    return model.customerIDs.length
  }

  return Number(model?.customerCount || 0)
}

const campaignHasCustomers = (model: any) => {
  return campaignCustomerCount(model) > 0
}

const notifyNoCustomers = () => {
  $notification.error({ errors: ['No customers'] })
}

const validate = async () => {
  return await form.value.validate()
}

const fetchCampaign = async () => {
  try {
    let c
    const id = props.id

    if (id) {
      c = (await $api.campaign.get(id)).data
    } else {
      c = (await $api.campaign.defaults({} as any)).data
      c.type = null

      if (props.customers) {
        c.customerIDs = props.customers
      }
    }

    if (c.contentType === null) {
      c.contentType = CampaignContentType.marketing
    }

    campaign.value = c
    snapshot()
    loading.value = false
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
    router.push('/marketing/campaigns')
  }
}

const fetchAssets = async () => {
  if (!$grants.all({ marketing: ['view'] })) return

  try {
    // TODO: Migrate listRunner pattern - using marketing.uploads with manual pagination
    const result = await $api.marketing.uploads({ page: 1, dataType: 0 })

    if (result?.items) {
      assets.value = result.items
        .map((asset: any) => {
          return {
            thumb: asset.imageUrls?.square,
            url: asset.imageUrls?.large,
            id: asset.id,
            alt: asset.caption,
          }
        })
    }
  } catch (e: any) {
    console.log(e)
  }
}

const handlePreview = async () => {
  const valid = await validate()

  if (valid) {
    try {
      const saved = (await $api.campaign.save(campaign.value)).data
      campaign.value = saved
      const success = await $api.campaign.sendPreview(campaign.value)
      if (success) {
        const emp = $auth.info.employee
        $notification.toast({
          title: $i18n.t('pages.marketing.campaigns.edit.previewSuccess'),
          text: isCampaignTypeEmail.value ? $i18n.t('pages.marketing.campaigns.edit.previewSentToEmail', { email: emp.email }) : $i18n.t('pages.marketing.campaigns.edit.previewSentToPhone', { phone: emp.phone }),
          variant: 'success',
        })
      }
    } catch (e: any) {
      console.log(e)
      $notification.error({ errors: e?.errors })
    }
  }
}

const handleSetType = (type: string) => {
  const contentType = (CampaignContentType as any)[type]

  if (contentType !== undefined) {
    campaign.value.contentType = contentType
  }
}

const campaignTypeItems = computed(() => [[
  {
    label: $i18n.t('general.marketing'),
    description: $i18n.t('marketing.notToUnsubscribed'),
    icon: 'lucide:megaphone',
    onSelect: () => handleSetType('marketing'),
  },
  {
    label: $i18n.t('marketing.administrative'),
    description: $i18n.t('marketing.sendToAll'),
    icon: 'lucide:triangle-alert',
    onSelect: () => handleSetType('administrative'),
  },
]])

const handleCreate = async (type: string) => {
  loading.value = true

  campaign.value.type = (CampaignType as any)[type]

  if (type === 'email') {
    campaign.value.filters = {
      massEmailSubscription: 'subscribed',
      hasEmail: 'true',
    }
  }

  if (type === 'sms') {
    campaign.value.filters = {
      massSMSSubscription: 'subscribed',
      hasPhone: 'true'
    }
  }

  await handleSave()

  loading.value = false
}

const handleSend = async (schedule = false) => {
  const valid = await validate()

  if (valid) {
    if (!campaignHasCustomers(campaign.value)) {
      notifyNoCustomers()
      return
    }

    loading.value = true

    try {
      const saved = (await $api.campaign.save(campaign.value)).data
      campaign.value = saved

      if (!campaignHasCustomers(campaign.value)) {
        notifyNoCustomers()
        loading.value = false
        return
      }

      loading.value = false
      sendDialog.value.open(campaign.value, schedule)
    } catch (e: any) {
      console.log(e)
      $notification.error({ errors: e?.errors })
      loading.value = false
    }
  }
}

const handleUpload = async (upload: any, data: any) => {
  loading.value = true

  try {
    const attachment = data.files[0]

    const c = (await $api.campaign.addAttachment(campaign.value, { attachment })).data

    if (c) {
      campaign.value.attachments = c.attachments
      loading.value = false

      const file = c.attachments[c.attachments.length - 1]

      if (file) {
        const response = {
          file: {
            url: file.url,
            id: file.guid,
          },
        }

        upload.complete(response, data.e)
      }
    }
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
    return false
  }

  loading.value = false
}

const handleAddAttachment = () => {
  addAttachmentDialog.value.open(campaign.value)
}

const isImageAttachment = (attachment: any) => {
  return Boolean(attachment?.isImage || attachment?.type?.includes('image'))
}

const handleRemoveAttachment = async (attachment: any) => {
  try {
    loading.value = true
    const c = (await $api.campaign.removeAttachment(campaign.value, attachment)).data
    if (c) {
      campaign.value.attachments = c.attachments

      $notification.toast({
        title: $i18n.t('pages.marketing.campaigns.edit.removedSuccess'),
        text: $i18n.t('pages.marketing.campaigns.edit.removedSuccessInfo', { name: attachment.name }),
        variant: 'success',
      })
    }
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
  }

  loading.value = false
}

const handleInsertAttachment = (attachment: any) => {
  const image = {
    [attachment.name]: {
      url: attachment.url,
    },
  }

  // insert
  article.value.insertImage(image)
}

const handleSave = async (close = false) => {
  loading.value = true

  try {
    const saved = (await $api.campaign.save(campaign.value)).data
    if (saved) {
      campaign.value = saved
      $notification.toast({
        title: $i18n.t('pages.marketing.campaigns.edit.saveSuccess'),
        text: $i18n.t('pages.marketing.campaigns.edit.saveSuccessInfo'),
        variant: 'success',
      })

      if (close) {
        panel.close(true)
      }

      $bus.emit.updated('Campaign', campaign.value)
    }
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
  }

  loading.value = false
}

const cancel = (force = false) => {
  if (!force && unsavedChanges.value) {
    confirmDialog.value.open()
  } else {
    panel.close(force)
  }
}

const init = async () => {
  await fetchCampaign()
  await fetchAssets()

  ready.value = true
}

init()
</script>
